import React, { Component } from 'react'

import { BrowserRouter, Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom"
import { useEffect } from 'react'
import { Typeahead } from 'react-bootstrap-typeahead'
import { GlobalHotKeys } from 'react-hotkeys'
import { X, Search, Menu } from 'react-feather'

import { Badge } from '@/components/ui/badge'
import { TooltipProvider } from '@/components/ui/tooltip'

import Tools from './utils/Tools'
import Pages from './utils/Pages'
import Theme from './utils/Theme'
import Version from './resources/Version'
import Preferences from './utils/Preferences'
import ButtonTooltip from './utils/ButtonTooltip'
import Utils from './utils/Utils'
import twitterConfig from './utils/TwitterConfig'
import MostFrequentlyUsed from './utils/MostFrequentlyUsed'

import About from './pages/About'
import Settings from './pages/Settings'
import NotFound from './pages/NotFound'

import './App.css'
import HomeIcon from './homeicon.svg'

// Bridges useNavigate/useLocation hooks into the App class component
function RouterHelpers({ navigateRef, onHistoryChange }) {
    const navigate = useNavigate()
    const location = useLocation()
    useEffect(() => { navigateRef.current = navigate }, [navigate, navigateRef])
    useEffect(() => { onHistoryChange(location) }, [location, onHistoryChange])
    return null
}

class App extends Component {
    constructor(props) {
        super(props)

        Theme.apply(Theme.getIsDark())

        this.state = {
            selected: [],
            noticeVisible: Preferences.getValue('/', 'home', 'noticeVisible', true),
            orientation: window.screen.orientation,
            menuOpen: false,
        }

        this.matchesTool = this.matchesTool.bind(this)
        this.onSearchFocus = this.onSearchFocus.bind(this)
        this.onSearchBlur = this.onSearchBlur.bind(this)
        this.onSearchChange = this.onSearchChange.bind(this)
        this.onSearchKeyDown = this.onSearchKeyDown.bind(this)
        this.onNoticeDismiss = this.onNoticeDismiss.bind(this)

        // handles screen orientation; since the "notch" seems to be always
        // on the top of the screen, we can have expand to the end of left edge
        // when the notch is on the right, and the other way around when the
        // notch is on the left; just this API doesn't work on all browsers;
        this.onOrientationChange = this.onOrientationChange.bind(this)

        this.onMenuToggle = this.onMenuToggle.bind(this)
        this.onHistoryChange = this.onHistoryChange.bind(this)
        this._navigate = { current: null }

        this.tools = Tools.getTools()
        this.pages = Pages.getPages()
        this.navPages = Pages.getTopNavPages()

        this.searchList = this.tools.map(tool => {
            return {
                id: tool.link,
                label: tool.title,
                link: tool.link,
                tags: [], // FIXME ToolsUtil.getToolTags(tool.title),
            }
        })
        this.pages.forEach(page => {
            this.searchList.push({
                id: page.getLink(),
                label: page.getSearchTitle(),
                link: page.getLink(),
                tags: page.getTags(),
            })
        })
        this.mfu = new MostFrequentlyUsed(
            "apps",
            this.searchList.map(t => t.link)
        )

        this.keyHandlers = {
            SEARCH: this.onSearchFocus,
        }
    }

    static KEY_MAP = {
        SEARCH: "/",
    }

    componentDidMount() {
        window.screen.onorientationchange = this.onOrientationChange
        window.onresize = this.onOrientationChange

        if (window.matchMedia) {
            this._mql = window.matchMedia('(prefers-color-scheme: dark)')
            this._mqlListener = () => {
                if (Theme.getSaved() === 'system') {
                    Theme.apply(this._mql.matches)
                }
            }
            this._mql.addEventListener('change', this._mqlListener)
        }
    }

    componentWillUnmount() {
        if (this._mql && this._mqlListener) {
            this._mql.removeEventListener('change', this._mqlListener)
        }
    }

    onOrientationChange() {
        this.setState(Object.assign({}, this.state, {
            orientation: window.screen.orientation,
        }))
    }

    onHistoryChange(location) {
        this.mfu.use(location.pathname)
        if (typeof gtag === 'function') {
            gtag('event', 'page_view', { page_path: location.pathname })
        }
    }

    matchesTool(option, props) {
        if (!props.text) {
            return false
        }
        const keyword = props.text.toLowerCase()
        const labelLower = option.label.toLowerCase()
        const search = [
            labelLower.split(/ /).join(''),
            labelLower
        ]
        if (option.tags) {
            option.tags.forEach(tag => {
                const tagLower = tag.toLowerCase()
                search.push(tagLower.split(/ /).join(''))
                search.push(tagLower)
            })
        }
        for (const s of search) {
            if (s.indexOf(keyword) >= 0) {
                return true
            }
        }
        return false
    }

    onSearchFocus(event) {
        if (this._typeahead) {
            if (event) {
                event.preventDefault()
            }
            this._typeahead.focus()
        }
    }
    onSearchBlur(event) {
        if (this._typeahead) {
            if (event) {
                event.preventDefault()
            }
            this._typeahead.clear()
            this._typeahead.blur()
        }
    }
    onSearchChange(selected) {
        this.setState(Object.assign({}, this.state, {
            selected: [],
        }))
        if (selected && selected.length > 0) {
            this._navigate.current && this._navigate.current(selected[0].link)
            this.onSearchBlur()
        }
    }
    onSearchKeyDown(event) {
        if (event) {
            if (event.key === 'Escape') {
                this.onSearchBlur(event)
            }
            else if (event.key === 'Enter') {
                // using undocumented internal state.initialItem
                // which seems to be filled in when there's a list
                // in the menu displayed on screen.
                if (this._typeahead.state
                && !this._typeahead.state.activeItem
                && this._typeahead.state.initialItem) {
                    this.onSearchChange([this._typeahead.state.initialItem])
                }
            }
        }
    }
    onMenuToggle() {
        this.setState({ menuOpen: !this.state.menuOpen })
    }

    onNoticeDismiss() {
        const visible = false
        Preferences.setValue('/', 'home', 'noticeVisible', visible)
        this.setState(Object.assign({}, this.state, {
            noticeVisible: visible,
        }))
    }

    render() {
        const sortedSearchList = this.searchList.slice()
        sortedSearchList.sort((a, b) => {
            return this.mfu.compare(a.link, b.link) * -1
        })

        const notchStyle = Utils.getNotchStyle()
        const self = this
        return (
            <TooltipProvider>
            <BrowserRouter>
                <RouterHelpers navigateRef={this._navigate} onHistoryChange={this.onHistoryChange} />
                <div className="App">
                    {/* Sticky header */}
                    <nav className={ "sticky top-0 z-50 w-full bg-zinc-900 border-b border-zinc-700 " + notchStyle }>
                        <div className="flex items-center gap-3 px-3 py-2">
                            {/* Logo */}
                            <NavLink to="/" className="flex items-center gap-2 shrink-0 text-white hover:text-white no-underline">
                                <img src={ HomeIcon } className="App-logo" alt="logo" />
                                <span className="font-semibold text-sm">appler.dev</span>
                            </NavLink>

                            {/* Search */}
                            <div className="flex-1 min-w-0 max-w-sm relative group/search">
                                <Search
                                    size={14}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 group-focus-within/search:text-zinc-300 transition-colors duration-200 pointer-events-none z-10" />
                                <Typeahead
                                    id="Tool Search"
                                    placeholder="Search tools..."
                                    onChange={ this.onSearchChange }
                                    onKeyDown={ this.onSearchKeyDown }
                                    options={ sortedSearchList }
                                    selected={ this.state.selected }
                                    filterBy={ this.matchesTool }
                                    ref={ (ref) => self._typeahead = ref } />
                                <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 font-mono leading-none pointer-events-none opacity-100 group-focus-within/search:opacity-0 transition-opacity duration-150">/</kbd>
                            </div>

                            {/* Nav icons — desktop */}
                            <div className="ml-auto hidden sm:flex items-center gap-1">
                                { this.navPages.map((nav) => {
                                    return <NavLink to={ nav.getLink() }
                                        key={ `navlink-${nav.getTitle()}` }
                                        id={ "top" + nav.getTitle() }
                                        data-cy={ nav.getTitle() }
                                        className={({ isActive }) => "no-underline inline-flex items-center justify-center rounded p-1 " + (isActive ? "text-white bg-zinc-700" : "text-zinc-300 hover:text-white")}>
                                        <ButtonTooltip placement="auto" tooltipMessage={ nav.getTitle() }>
                                            { nav.getNavIcon() }
                                        </ButtonTooltip>
                                    </NavLink>
                                })}
                            </div>

                            {/* Hamburger — mobile */}
                            <div className="ml-auto sm:hidden relative">
                                { this.state.menuOpen && (
                                    <div className="fixed inset-0 z-40" onClick={ () => this.setState({ menuOpen: false }) } />
                                )}
                                <button
                                    onClick={ this.onMenuToggle }
                                    className="relative z-50 inline-flex items-center justify-center rounded p-1 text-zinc-300 hover:text-white">
                                    { this.state.menuOpen ? <X size={18} /> : <Menu size={18} /> }
                                </button>
                                { this.state.menuOpen && (
                                    <div className="absolute right-0 top-full mt-1 z-50 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl py-1 min-w-[160px]">
                                        { this.navPages.map((nav) => (
                                            <NavLink
                                                key={ nav.getTitle() }
                                                to={ nav.getLink() }
                                                onClick={ () => this.setState({ menuOpen: false }) }
                                                className={({ isActive }) => "flex items-center gap-3 px-4 py-2.5 text-sm no-underline " + (isActive ? "text-white bg-zinc-700" : "text-zinc-300 hover:text-white hover:bg-zinc-700/50")}>
                                                { nav.getNavIcon() }
                                                <span>{ nav.getTitle() }</span>
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </nav>

                    {/* Welcome banner */}
                    { this.state.noticeVisible &&
                        <div
                            id="announcement"
                            className={ "relative m-2 rounded-lg border bg-green-50 border-green-300 text-green-900 dark:bg-green-900/30 dark:border-green-700 dark:text-green-200 px-4 py-3 text-sm " + notchStyle }>
                            <button
                                onClick={ this.onNoticeDismiss }
                                className="absolute top-2 right-2 text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-100 p-1"
                                aria-label="Dismiss">
                                <X size={16} />
                            </button>
                            <p className="font-semibold mb-1">Welcome — I have just a few comments</p>
                            <ul className="list-disc list-inside space-y-1">
                                <li>This is a single page app (SPA) and will do all the work in your browser, which
                                means <i><strong>all the data stays in your browser (whether entered or generated by the tool),
                                and would never be sent anywhere</strong></i>.</li>
                                <li>If you have found any bugs or just wish to talk to me, reach out through <a href={ twitterConfig.url } className="underline">{ twitterConfig.handle }</a> on X (DM is
                                open to everyone).</li>
                                <li>Check out <NavLink to={ About.getLink() } className="underline">About...</NavLink> and <NavLink to={ Settings.getLink() } className="underline">Settings</NavLink> page
                                for more.</li>
                            </ul>
                        </div>
                    }

                    <Routes>
                        { this.pages.map( page => {
                            return <Route key={ page.getLink() } path={ page.getLink() } element={ React.createElement(page) } />
                        }) }
                        { this.tools.map( tool => {
                            return <Route key={ tool.link } path={ tool.link } element={ React.createElement(tool.async) } />
                        }) }
                        <Route path="*" element={ <NotFound /> } />
                    </Routes>

                    {/* Footer */}
                    <div id="footer" className={ "flex justify-end items-center gap-3 px-4 py-2 border-t bg-muted/30 text-sm text-muted-foreground " + notchStyle }>
                        <span>
                            Copyright 2026 <a href="https://x.com/appler" target="_blank" rel="noopener noreferrer" className="hover:underline">@appler</a>
                        </span>
                        { !!Version.commitHash &&
                            <span className="small-text text-xs italic text-muted-foreground/60">
                                { Version.commitHash }
                            </span>
                        }
                    </div>

                    <GlobalHotKeys keyMap={ App.KEY_MAP } handlers={ this.keyHandlers } />
                </div>
            </BrowserRouter>
            </TooltipProvider>
        )
    }
}

export default App
